<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-10-27 (0.1.0) (jhoward)
 *   - Add SmsOptedOutNotice: tells a guest whose number stopped texts from us how to start them again - text START to
 *     our number, with a link that opens the text ready to send - and offers to try again.
 */

import MaterialButton from '@framework/components/material/MaterialButton.vue';
import type { PropType } from 'vue';
import type { SmsOptIn } from '@/helpers/smsOptedOut';

defineProps({
  optIn: {
    type: Object as PropType<SmsOptIn>,
    required: true,
  },
  /** The try-again button's label. Leave it out where the screen's own button already sends the code again. */
  retryLabel: {
    type: String,
    default: '',
  },
});

defineEmits<{
  (event: 'retry'): void;
}>();
</script>

<template>
  <div class="sms_opted_out" role="status">
    <p>
      <span class="material-icons" aria-hidden="true">speaker_notes_off</span>
      <span>
        <strong>Texts to your phone are turned off.</strong> A STOP reply to one of our texts turned them off, so we
        can't text you a code until they are turned back on.
      </span>
    </p>
    <ol>
      <li>
        Text <strong>{{ optIn.keyword }}</strong> to
        <a v-if="optIn.href && optIn.number" :href="optIn.href">{{ optIn.number }}</a>
        <template v-else>the number our texts come from</template>.
      </li>
      <li>Then {{ retryLabel ? `tap ${retryLabel.toLowerCase()}` : 'send the code again' }}.</li>
    </ol>
    <!-- type="button": the notice can sit inside a code form, and an untyped button there would submit it. -->
    <material-button
      v-if="retryLabel"
      type="button"
      :label="retryLabel.toUpperCase()"
      @click="$emit('retry')"
    ></material-button>
  </div>
</template>

<style scoped>
.sms_opted_out p {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.sms_opted_out ol {
  margin: 0.5em 0 1em;
}

.sms_opted_out a {
  white-space: nowrap;
}
</style>
